import React, { useEffect } from "react";

import { useDispatch, useSelector } from "react-redux";
import { Route } from "react-router";
import { Switch, useLocation } from "react-router-dom";
import ym, { YMInitializer } from "react-yandex-metrika";
import { NumberParam, useQueryParam } from "use-query-params";

import { Layout, Spin } from "antd";

import { HttpErrorPage, LoginPage } from "../../pages";
import { VIEW_GATES_PAGE_PERM } from "../../permissions";
import { ApplicationRoute, LoginRoute, ProtectedRoute } from "../../routes";
import { readAlertRequest, unreadAlertsFetchRequest } from "../../store/alerts/actions";
import { gatesFetchRequest } from "../../store/gates/actions";
import { marketsFetchRequest } from "../../store/markets/actions";
import { RootState } from "../../store/root_reducer";
import { userFetchRequest } from "../../store/user/actions";
import { AdminLayout, UserLayout } from "../layouts";

import { AlertModal } from "./components";

export const ProtectedApplication = () => {
  const [gateId] = useQueryParam("gateId", NumberParam);

  const alertsState = useSelector((state: RootState) => state.Alerts);
  const userIsLoaded = useSelector((state: RootState) => state.User.isLoaded);
  const isGatesLoaded = useSelector((state: RootState) => state.Gates.isLoaded);
  const selectedGate = useSelector((state: RootState) => state.User.selectedGate);
  const permissions = useSelector((state: RootState) => state.User.permissions);

  const dispatch = useDispatch();
  useEffect(() => {
    // eslint-disable-next-line no-nested-ternary
    const queryGateId = selectedGate.id === 0 ? (gateId === null ? undefined : gateId) : selectedGate.id;
    dispatch(userFetchRequest(queryGateId));
    // eslint-disable-next-line
  }, [dispatch]);

  useEffect(() => {
    dispatch(marketsFetchRequest({ gateId: selectedGate.id }));
  }, [dispatch, selectedGate]);

  useEffect(() => {
    if (!isGatesLoaded && permissions.includes(VIEW_GATES_PAGE_PERM)) {
      dispatch(gatesFetchRequest());
    }
  }, [dispatch, isGatesLoaded, permissions]);

  useEffect(() => {
    dispatch(unreadAlertsFetchRequest());
  }, [dispatch]);

  const handleAlertModalOk = (id: number) => () => dispatch(readAlertRequest({ id }));
  const currentAlert = alertsState.unreadAlerts ? alertsState.unreadAlerts[0] : null;

  return !userIsLoaded ? (
    <Spin size="large" style={{ margin: "auto" }} />
  ) : (
    <>
      {currentAlert && (
        <AlertModal alert={currentAlert} isLoading={alertsState.isLoading} handleOk={handleAlertModalOk} />
      )}
      <Switch>
        <Route path={ApplicationRoute.Admin} component={AdminLayout} />
        <Route path={ApplicationRoute.Base} component={UserLayout} />
      </Switch>
    </>
  );
};

export const Application: React.FC = () => {
  const location = useLocation();
  useEffect(() => ym("hit", location.pathname), [location.pathname]);

  return (
    <Layout style={{ minHeight: "100vh" }}>
      <YMInitializer
        accounts={[81871495]}
        options={{ clickmap: true, trackLinks: true, accurateTrackBounce: true, webvisor: true, defer: true }}
      />
      <Switch>
        <LoginRoute exact path={ApplicationRoute.Login} component={LoginPage} />
        <Route exact path={ApplicationRoute.Forbidden} render={(props) => <HttpErrorPage code={403} {...props} />} />
        <Route exact path={ApplicationRoute.NotFound} render={(props) => <HttpErrorPage code={404} {...props} />} />
        <ProtectedRoute path={ApplicationRoute.Base} component={ProtectedApplication} />
      </Switch>
    </Layout>
  );
};
